import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, Heart } from 'lucide-react';
import { ELI_ELY } from '../../data/eliEly';
import { Reveal, Eyebrow } from '../../components/Reveal';
import { Label } from '../../components/u99/Label';

const CharacterCard = ({ c, i }) => (
  <Reveal delay={i * 0.08} className={c.id === 'eli' ? 'md:col-span-2' : ''}>
    <article className="h-full rounded-3xl border p-6 lg:p-8 relative overflow-hidden" style={{ borderColor: `${c.color}40`, background: `linear-gradient(180deg, ${c.color}0a, transparent 60%)` }} data-testid={`character-card-${c.id}`}>
      <span className="absolute -right-3 -top-4 font-celestial text-[6rem] leading-none select-none" style={{ color: `${c.color}12` }}>{c.name[0]}</span>
      <p className="font-celestial text-3xl tracking-wide" style={{ color: c.color }}>{c.name}</p>
      {c.alias && <p className="mt-1 font-mono-precision text-[11px] uppercase tracking-[0.25em] text-slate-300">{c.alias}</p>}
      <dl className="mt-6 space-y-3">
        {c.facts.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[90px_1fr] gap-3">
            <dt className="font-mono-precision text-[10px] uppercase tracking-[0.2em] pt-1" style={{ color: c.color }}>{k}</dt>
            <dd className="text-sm sm:text-base text-slate-200 leading-relaxed">{v}</dd>
          </div>
        ))}
      </dl>
      {c.is.length > 0 && (
        <div className="mt-7 rounded-2xl border border-white/[0.08] bg-[#04070f]/70 p-5">
          <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em]" style={{ color: c.color }}>{c.name === 'AUNTY' ? 'Tanya is…' : `${c.name.charAt(0) + c.name.slice(1).toLowerCase()} is…`}</p>
          <ul className="mt-3 space-y-1.5">{c.is.map((t) => <li key={t} className="flex gap-2 text-sm text-slate-300"><Heart size={12} className="mt-1 shrink-0" style={{ color: c.color }} /> {t}</li>)}</ul>
        </div>
      )}
      {c.role && (
        <div className="mt-4 rounded-2xl border border-amber-400/20 bg-amber-400/[0.04] p-5">
          <p className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-amber-300">Her role</p>
          <p className="mt-2 text-sm sm:text-base text-amber-50/90 leading-relaxed">{c.role}</p>
        </div>
      )}
      {c.polaroids.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          {c.polaroids.map((p) => <span key={p} className="font-mono-precision text-[9px] uppercase tracking-[0.2em] text-slate-400 border border-white/10 rounded-full px-3 py-1">{p}</span>)}
        </div>
      )}
    </article>
  </Reveal>
);

export default function EliElyWorld() {
  useEffect(() => { document.title = 'The Characters — ELI & ELY: THE SPLIT PART ONE'; }, []);
  const w = ELI_ELY;
  return (
    <main className="min-h-screen" data-testid="eli-ely-world-page">
      <header className="relative pt-32 pb-14 lg:pt-44 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"><img src={w.image} alt="" className="w-full h-full object-cover opacity-30" /><div className="absolute inset-0 bg-gradient-to-b from-[#02040a]/70 via-[#02040a]/70 to-[#02040a]" /></div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <Link to={`/u99/documents/${w.slug}`} className="inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.3em] text-slate-500 hover:text-amber-300 transition-colors" data-testid="world-back-link"><ArrowLeft size={12} /> {w.title}</Link>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <span className="font-mono-precision text-xs uppercase tracking-[0.3em] text-amber-300">World building · Character map</span>
            <Label id="creative-doctrine" size="xs" />
          </div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="mt-6 font-celestial text-4xl sm:text-5xl lg:text-7xl font-light text-slate-100 leading-[1.02]" data-testid="world-title">The Characters</motion.h1>
          <p className="mt-4 font-celestial text-xl text-amber-200/80 italic">of {w.title}</p>
          <p className="mt-6 font-celestial text-lg sm:text-xl text-slate-300"><span className="text-slate-500 italic">by</span> <span className="text-amber-100">Kyle Hinton</span></p>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-5 lg:px-10">
        <motion.figure initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.2 }} className="rounded-3xl overflow-hidden border border-amber-400/20 shadow-[0_60px_120px_-40px_rgba(245,158,11,0.3)]" data-testid="world-character-map">
          <img src={w.image} alt="Character map — Emily, Uncle Marcus, Aunty Tanya" className="w-full h-auto block" />
        </motion.figure>

        <div className="mt-20">
          <Reveal><Eyebrow>The family</Eyebrow></Reveal>
          <div className="grid md:grid-cols-2 gap-5">
            {w.characters.map((c, i) => <CharacterCard key={c.id} c={c} i={i} />)}
          </div>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-5">
          <Reveal className="lg:col-span-7">
            <div className="h-full rounded-3xl border border-white/[0.08] p-7 lg:p-10" data-testid="world-family-vibes">
              <p className="font-mono-precision text-[10px] uppercase tracking-[0.3em] text-sky-300">Family vibes</p>
              <p className="mt-5 font-celestial text-2xl sm:text-3xl lg:text-4xl text-slate-100 leading-snug">{w.familyVibes}</p>
              <p className="mt-8 font-mono-precision text-[10px] uppercase tracking-[0.3em] text-sky-300">Extra notes</p>
              <ul className="mt-4 space-y-2.5">{w.extraNotes.map((n) => <li key={n} className="flex gap-3 text-sm sm:text-base text-slate-300 leading-relaxed"><span className="text-sky-300 mt-1">★</span>{n}</li>)}</ul>
            </div>
          </Reveal>
          <div className="lg:col-span-5 grid gap-5">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-white/[0.08] p-7" data-testid="world-their-world">
                <p className="font-mono-precision text-[10px] uppercase tracking-[0.3em] text-fuchsia-300">Their world</p>
                <div className="mt-4 flex flex-wrap gap-2">{w.theirWorld.map((t) => <span key={t} className="font-celestial text-sm text-slate-200 border border-white/10 rounded-full px-4 py-1.5">{t}</span>)}</div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-3xl border border-amber-400/20 bg-amber-400/[0.03] p-7" data-testid="world-what-matters">
                <p className="font-mono-precision text-[10px] uppercase tracking-[0.3em] text-amber-300">What matters most</p>
                <ul className="mt-4 space-y-2">{w.whatMatters.map((t) => <li key={t} className="flex gap-2 text-sm text-slate-200"><Heart size={12} className="mt-1 text-amber-300 shrink-0" />{t}</li>)}</ul>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal className="mt-16 mb-28 lg:mb-40">
          <Link to={`/u99/documents/${w.slug}`} className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-3xl border border-amber-400/30 hover:border-amber-300/60 p-7 lg:p-10 transition-colors" data-testid="world-read-novel-link">
            <div>
              <p className="font-mono-precision text-[10px] uppercase tracking-[0.3em] text-amber-300">The novel</p>
              <p className="mt-3 font-celestial text-2xl sm:text-3xl text-slate-100 group-hover:text-amber-100 transition-colors">{w.title}</p>
              <p className="mt-2 font-celestial text-base text-slate-400"><span className="italic">by</span> Kyle Hinton</p>
            </div>
            <span className="inline-flex items-center gap-3 font-mono-precision text-[11px] uppercase tracking-[0.25em] text-amber-300"><BookOpen size={14} /> Open the publication</span>
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
